// Período selecionado no topo. Mês é o padrão; ano e intervalo livre cobrem quem
// olha o longo prazo; "mês começa no dia N" atende quem controla do salário ao salário.
import { monthCycleForKey, monthKeyForDate } from '@/lib/monthCycle';
import { addMonths, dayShort, monthLong, monthShort, cap } from './format';

export type PeriodSel =
  | { kind: 'month'; ym: string }
  | { kind: 'year'; year: number }
  | { kind: 'range'; from: string; to: string };

export interface Period {
  sel: PeriodSel;
  from: string; // YYYY-MM-DD inclusive
  to: string;   // YYYY-MM-DD inclusive
  label: string;
  /** meses (YYYY-MM) que o período toca, em ordem */
  months: string[];
  /** mês de referência pra faturas e pra série de 12 meses */
  endMonth: string;
}

function pad(n: number) { return String(n).padStart(2, '0'); }
function daysIn(ym: string) { return new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0).getDate(); }
function clampDay(ym: string, day: number) { return `${ym}-${pad(Math.min(day, daysIn(ym)))}`; }
function shiftDay(iso: string, delta: number) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let ym = from.slice(0, 7);
  const end = to.slice(0, 7);
  while (ym <= end) { out.push(ym); ym = addMonths(ym, 1); }
  return out;
}

/** "2026-09-05" → "5 set" */
function dayMonth(iso: string) {
  return `${Number(iso.slice(8, 10))} ${monthShort(iso.slice(0, 7))}`;
}
function todayIso(today: Date) {
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

export function resolvePeriod(sel: PeriodSel, startDay = 1): Period {
  if (sel.kind === 'month') {
    if (startDay <= 1) {
      const from = `${sel.ym}-01`;
      const to = clampDay(sel.ym, 31);
      return { sel, from, to, label: cap(monthLong(sel.ym)), months: [sel.ym], endMonth: sel.ym };
    }
    // Período do app (ADR 0190), a mesma matemática do Orçamento (ADR 0120): a chave `ym` nomeia
    // o ciclo pela regra da maioria dos dias — dia até 15, o mês em que começa; 16 em diante, o
    // mês em que termina (dia 20: "2026-11" é 20/out–19/nov). Dia 31 em mês curto vira o último
    // dia. Quem usa vê o intervalo; a chave segue sendo o `endMonth` (faturas, série de 12 meses).
    const { startDate: from, endDate: to } = monthCycleForKey(sel.ym, startDay);
    return { sel, from, to, label: `${dayMonth(from)} – ${dayMonth(to)}`, months: monthsBetween(from, to), endMonth: sel.ym };
  }
  if (sel.kind === 'year') {
    // Com dia próprio o ano são os 12 PERÍODOS do ano, como no app (ADR 0190, item 9): do início
    // do período de janeiro ao fim do de dezembro.
    const calendar = startDay <= 1;
    const from = calendar ? `${sel.year}-01-01` : monthCycleForKey(`${sel.year}-01`, startDay).startDate;
    const to = calendar ? `${sel.year}-12-31` : monthCycleForKey(`${sel.year}-12`, startDay).endDate;
    return { sel, from, to, label: String(sel.year), months: monthsBetween(from, to), endMonth: `${sel.year}-12` };
  }
  const from = sel.from <= sel.to ? sel.from : sel.to;
  const to = sel.from <= sel.to ? sel.to : sel.from;
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const label = `${dayShort(from)}${sameYear ? '' : `/${from.slice(2, 4)}`} – ${dayShort(to)}/${to.slice(2, 4)}`;
  return { sel, from, to, label, months: monthsBetween(from, to), endMonth: to.slice(0, 7) };
}

/** Anda um passo pra frente/trás no mesmo tipo de período. */
export function stepPeriod(sel: PeriodSel, delta: 1 | -1): PeriodSel {
  if (sel.kind === 'month') return { kind: 'month', ym: addMonths(sel.ym, delta) };
  if (sel.kind === 'year') return { kind: 'year', year: sel.year + delta };
  const [fy, fm, fd] = sel.from.split('-').map(Number);
  const [ty, tm, td] = sel.to.split('-').map(Number);
  const len = Math.round((new Date(ty, tm - 1, td).getTime() - new Date(fy, fm - 1, fd).getTime()) / 86400000) + 1;
  return { kind: 'range', from: shiftDay(sel.from, delta * len), to: shiftDay(sel.to, delta * len) };
}

/** Período imediatamente anterior, do mesmo tamanho (pra "vs. período anterior"). */
export function previousPeriod(p: Period, startDay = 1): Period {
  return resolvePeriod(stepPeriod(p.sel, -1), startDay);
}

export function inPeriod(date: string, p: Pick<Period, 'from' | 'to'>): boolean {
  return date >= p.from && date <= p.to;
}

/** Chave do período de hoje: com dia 1 é o mês-calendário (`todayMonth`); com dia 20, em 25/10 já é "2026-11". */
export function currentMonthKey(startDay = 1, today: Date = new Date()): string {
  const iso = todayIso(today);
  return startDay <= 1 ? iso.slice(0, 7) : monthKeyForDate(iso, startDay);
}

/**
 * O mês em que a tela abre: hoje, no período do Dia de início. Se hoje está fora dos dados,
 * fica o `latestMonth` do modelo (o último mês com movimento), que já é uma chave.
 */
export function openingMonthKey(latestMonth: string, startDay = 1, today: Date = new Date()): string {
  return latestMonth === currentMonthKey(1, today) ? currentMonthKey(startDay, today) : latestMonth;
}
